/**
 * Sollae Cloud - Dynamic UI Architecture (DeviceSetting.js)
 * 솔래 클라우드 - 동적 UI 아키텍처 (DeviceSetting.js)
 *
 * [English]
 * - Purpose: Centralized configuration for the "Device Settings" menu.
 * - Logic: This file acts as a "UI Engine" that maps hardware parameters to specific UI components.
 * - Key Components:
 *   1. TYPE_XX (ENUMs): Defines the type of UI input (Select, Input, Button, Widget List, etc.).
 *   2. MENU_CODE: Unique identifiers mapping settings to the backend/device API.
 *   3. CATEGORY / TEMPLATES: Define the sidebar structure and port-specific UI layouts.
 *   4. CRITICAL FUNCTIONS:
 *      - createDeviceSettingData: Entry point for initializing UI templates.
 *      - getModifiedSetting: Maps reactive UI state to raw API payloads.
 *      - getSettingList: Generates the sequence of REST commands (POST/PUT/DELETE).
 *
 * [한국어]
 * - 설명: "장치 설정" 메뉴를 위한 중앙 집중식 구성 파일입니다.
 * - 로직: 이 파일은 하드웨어 파라미터를 특정 UI 컴포넌트에 매핑하는 "UI 엔진" 역할을 합니다.
 * - 주요 구성 요소:
 *   1. TYPE_XX (ENUMs): UI 입력 유형(Select, Input, Button, Widget List 등)을 정의합니다.
 *   2. MENU_CODE: 설정을 백엔드/장치 API와 매핑하는 고유 식별자입니다.
 *   3. CATEGORY / TEMPLATES: 사이드바 구조 및 포트별 UI 레이아웃을 정의합니다.
 *   4. 핵심 함수:
 *      - createDeviceSettingData: UI 템플릿 초기화를 위한 진입점입니다.
 *      - getModifiedSetting: 반응형 UI 상태를 원시 API 페이로드로 매핑합니다.
 *      - getSettingList: REST 명령어 시퀀스(POST/PUT/DELETE)를 생성합니다.
 */

/**
 * [DEV] SAVE LIFECYCLE & DATA FLOW
 * 이해를 돕기 위한 설정 저장 라이프사이클 및 데이터 흐름 가이드:
 *
 * [English]
 * 1. UI Interaction: User changes a setting in the 'Device Settings' modal.
 * 2. Component State: Local Vue component data (Settings Object) is updated.
 * 3. Vuex Dispatch: On 'Save', the component dispatches an action (e.g., `updateDeviceSetting`).
 * 4. PAYLOAD MAPPER (getModifiedSetting):
 *    - Converts Vue state into exact JSON format for API.
 * 5. API COMMAND GENERATOR (getSettingList):
 *    - Generates REST calls (POST, PUT, DELETE) based on diff.
 * 6. Final Sync: API response refreshes Vuex state.
 *
 * [한국어]
 * 1. UI 상호작용: 사용자가 '장치 설정' 모달에서 설정을 변경합니다.
 * 2. 컴포넌트 상태: 로컬 Vue 컴포넌트 데이터(Settings 객체)가 업데이트됩니다.
 * 3. Vuex 디스패치: '저장' 시 컴포넌트가 액션(예: `updateDeviceSetting`)을 디스패치합니다.
 * 4. 페이로드 매퍼 (getModifiedSetting):
 *    - Vue 상태를 API용 정밀 JSON 형식으로 변환합니다.
 * 5. API 명령어 생성기 (getSettingList):
 *    - 원본과의 차이점을 기반으로 REST 호출(POST, PUT, DELETE) 목록을 생성합니다.
 * 6. 최종 동기화: API 응답을 사용하여 글로벌 Vuex 상태를 갱신합니다.
 *
 * --------------------------------------------------------------------------
 * [DEV] UI EXTENSION GUIDE (설정 메뉴 확장 가이드)
 * --------------------------------------------------------------------------
 * [English]
 * 1. HOW TO ADD A NEW SETTING FIELD (e.g., a new Select Box):
 *    a. Define a new MENU_CODE if needed (maps to backend JSON key).
 *    b. Create a factory function using `createField`. You can use `createEnableField` as a reference.
 *    c. Add the field to the appropriate template (e.g., `ANALOG_INPUT_TEMPLATE`).
 *
 * 2. HOW TO ADD A NEW UI COMPONENT TYPE (e.g., TYPE_MYTYPE):
 *    a. Define a new `TYPE_XX` constant in both `DeviceSetting.js` and `DeviceSettingItem.vue`.
 *    b. Create your new Vue component in `src/components/device/items/`.
 *    c. Register and map the new component in `DeviceSettingItem.vue`'s `getComponent` computed property.
 *
 * 3. HOW TO ADD SETTINGS TO A SPECIFIC DEVICE MODAL (Template):
 *    a. Locate the template array for the target port type (e.g., `ANALOG_INPUT_TEMPLATE`).
 *    b. Append your new `SettingMenuItem` object to that array.
 *    c. Ensure the field is mapped in `getModifiedSetting()` to be included in the API payload.
 *
 * [한국어]
 * 1. 새로운 설정 필드 추가 방법 (예: 새로운 셀렉트 박스):
 *    a. 필요한 경우 새로운 MENU_CODE를 정의합니다 (백엔드 JSON 키와 매핑됨).
 *    b. `createField`를 사용하여 새로운 팩토리 함수를 만듭니다 (`createEnableField` 구현 참고).
 *    c. 적절한 템플릿(예: `ANALOG_INPUT_TEMPLATE`)에 필드를 추가합니다.
 *
 * 2. 새로운 UI 컴포넌트 타입 추가 방법 (예: TYPE_MYTYPE):
 *    a. `DeviceSetting.js`와 `DeviceSettingItem.vue` 모두에 새로운 `TYPE_XX` 상수를 정의합니다.
 *    b. `src/components/device/items/`에 새로운 Vue 컴포넌트를 생성합니다.
 *    c. `DeviceSettingItem.vue`의 `getComponent` 계산된 속성에서 새 컴포넌트를 등록하고 매핑합니다.
 *
 * 3. 특정 장치 모달(템플릿)에 설정 추가 방법:
 *    a. 대상 포트 유형에 대한 템플릿 배열(예: `ANALOG_INPUT_TEMPLATE`)을 찾습니다.
 *    b. 해당 배열에 새로운 `SettingMenuItem` 객체를 추가합니다.
 *    c. API 페이로드가 올바르게 생성되도록 `getModifiedSetting()`에서 필드를 매핑했는지 확인합니다.
 */

import {
  getBoolean,
  WGT_LIST,
  COMMAND_LIST,
  MIN_INT32,
  MAX_INT32,
  MIN_DEC_PLACE,
  MAX_DEC_PLACE,
  STS,
  ANALOG_INPUT,
  DIGITAL_INPUT,
  DIGITAL_OUTPUT,
  TEMPERATURE,
  SOLLAE_MODEL,
  // SOLLAE_BASE_URL,
  BAUD_RATE,
  PARITY,
  cloneObject,
  DATA_BIT,
  STOP_BIT,
  isModbusGateway,
  MAX_INTERFRAME_GAP,
  MIN_INTERFRAME_GAP,
} from './utils';

/**
 * [DEV] UI Component Types (TYPE_XX)
 *
 * [English]
 * Defines which Vue component should be rendered for a specific setting.
 *
 * [한국어]
 * 특정 설정에 대해 렌더링될 Vue 컴포넌트를 정의합니다.
 *
 * [GUIDE] To add a new type:
 * 1. Define here: `const TYPE_NEW = 15;`
 * 2. Update DeviceSettingItem.vue with the same constant and component mapping.
 *
 * [가이드] 새로운 타입을 추가하려면:
 * 1. 여기에 정의: `const TYPE_NEW = 15;`
 * 2. DeviceSettingItem.vue에서 동일한 상수와 컴포넌트 매핑을 업데이트합니다.
 */
const TYPE_INP = 0; // Text/Number Input Field (포트 설명, 카운터 초기값 등)
const TYPE_SEL = 1; // Select Dropdown (활성화/비활성화, 센서 타입 등)
const TYPE_TXT = 2; // Read-only Text Info (읽기 전용 텍스트. 예: AI Mode)
// const TYPE_RDB = 3; // Radio Button
const TYPE_CBB = 4; // Combobox (입력 및 선택 가능. 예: 단위 설정)
const TYPE_BAR = 5; // Color Picker / Bar (색상 선택기. 예: UI Color)
const TYPE_WGT = 6; // Widget Demo Display (위젯 미리보기 데모용 특수 타입)
const TYPE_TAB = 7; // Inner Tab Layout (내부 탭 레이아웃용)
const TYPE_BTN = 8; // Button Group (버튼 그룹. 예: 이벤트 설정 버튼)
const TYPE_CMD = 9; // Command Button Group (명령어 버튼. 예: 제어 버튼 테스트)
// const TYPE_LNK = 10; // Link
const TYPE_RAN = 11; // Range Input Min/Max (범위 입력. 예: 표시 스케일, 포트 범위)
const TYPE_SPR = 12; // Separator Line (구분선)
const TYPE_LST = 13; // Editable List (편집 가능한 목록. 예: 모드버스 슬레이브, 스케줄 리스트)
const TYPE_SLV = 14; // Unused

const MAX_STRING_LEN = 32;
const MAX_UNIT_STR_LEN = 8;

const API_PREFIX = process.env.API_PREFIX;

export const FORMAT = {
  STRING: 0,
  INT: 1,
  FLOAT: 2,
};

const { CMD_OUTPUT_LOW, CMD_OUTPUT_HIGH, CMD_OUTPUT_TOGGLE, CMD_OUTPUT_PULSE } =
  COMMAND_LIST;

const {
  WGT_AI_CBAR,
  WGT_AI_CHART,
  WGT_AI_THERMO,
  WGT_AI_GAUGE,
  WGT_AI_TEXT,
  WGT_AI_DROPLET,
  WGT_AI_HBAR,
  WGT_AI_VBAR,
  WGT_AI_7SEG,
  WGT_DI_CHART,
  WGT_DI_DLED,
  WGT_DI_COUNTER,
  WGT_DO_PLED,
  WGT_DO_SCHL,
  WGT_DO_CHART,
  WGT_DO_AMY,
} = WGT_LIST;

// Current color list design
const COLOR_LIST = [
  '#a43434',
  '#df691b',
  '#ebb311',
  '#169804',
  '#2c80af',
  '#7914a9',
  '#171c86',
];

// Legacy color list from Cloud V2
export const V2_COLOR_LIST = [
  '#c0392b',
  '#e67e22',
  '#f1c40f',
  '#16a085',
  '#2980b9',
  '#9b59b6',
  '#2c3e50',
];

// Define icon for each category
const ICON_CAT_GENERAL = require('@/assets/img/icon_cat_general.png');
const ICON_CAT_DEVICE = require('@/assets/img/icon_cat_device.png');
const ICON_CAT_WIDGET = require('@/assets/img/icon_cat_widget.png');
const ICON_CAT_EVENT = require('@/assets/img/icon_cat_event.png');
const ICON_CAT_ACTION = require('@/assets/img/icon_cat_action.png');
const ICON_CAT_SCHEDULE = require('@/assets/img/icon_cat_schedule.png');
const ICON_CAT_SERIAL = require('@/assets/img/icon_cat_serial.png');
const ICON_CAT_MODBUS = require('@/assets/img/icon_cat_modbus.png');
const ICON_CAT_QUERY = require('@/assets/img/icon_cat_query.png');

// Define icon for each DO command
const ICON_CMD_TOGGLE = require('@/assets/img/icon_toggle.png');
const ICON_CMD_ON = require('@/assets/img/icon_on.png');
const ICON_CMD_OFF = require('@/assets/img/icon_off.png');
const ICON_CMD_PULSE_HIGH = require('@/assets/img/icon_on_pulse.png');

export const LISTCOMMAND_NO = [
  {
    text: 'ON/OFF',
    hasPwm: false,
    type: CMD_OUTPUT_TOGGLE,
    icon: ICON_CMD_TOGGLE,
  },
  { text: 'ON', hasPwm: false, type: CMD_OUTPUT_HIGH, icon: ICON_CMD_ON },
  { text: 'OFF', hasPwm: false, type: CMD_OUTPUT_LOW, icon: ICON_CMD_OFF },
  {
    text: 'PULSE',
    hasPwm: true,
    type: CMD_OUTPUT_PULSE,
    icon: ICON_CMD_PULSE_HIGH,
  },
];

export const LISTCOMMAND_NC = [
  {
    text: 'ON/OFF',
    hasDelay: true,
    hasPwm: false,
    type: CMD_OUTPUT_TOGGLE,
    icon: ICON_CMD_TOGGLE,
  },
  {
    text: 'ON',
    hasDelay: true,
    hasPwm: false,
    type: CMD_OUTPUT_LOW,
    icon: ICON_CMD_ON,
  },
  {
    text: 'OFF',
    hasDelay: true,
    hasPwm: false,
    type: CMD_OUTPUT_HIGH,
    icon: ICON_CMD_OFF,
  },
  {
    text: 'PULSE',
    hasDelay: true,
    hasPwm: true,
    type: CMD_OUTPUT_PULSE,
    icon: ICON_CMD_PULSE_HIGH,
  },
];

export const EVENTLIST_AI = [
  { value: '>=', text: 'SETTING.str18' },
  { value: '<=', text: 'SETTING.str19' },
];
export const EVENTLIST_DI = [
  { value: '==1', text: 'SETTING.str22' },
  { value: '==0', text: 'SETTING.str21' },
  { value: '>=', text: 'SETTING.str39' },
  { value: '<=', text: 'SETTING.str65' },
];
export const EVENTLIST_DO = [
  { value: '==1', text: 'SETTING.str22' },
  { value: '==0', text: 'SETTING.str21' },
];

/**
 * [DEV] Menu Codes (MENU_CODE)
 * Maps the UI field back to the specific JSON parameter expected by the Device API.
 * UI 필드를 장치 API에서 기대하는 특정 JSON 파라미터(key)에 매핑합니다.
 *
 * [DEV] SettingMenuItem Object Structure (설정 메뉴 항목 객체 구조)
 * [English]
 * Defines the schema for objects inside template arrays (e.g., ANALOG_INPUT_TEMPLATE).
 * - name: (String) Translation key for the label (e.g., 'SETTING.str02').
 * - type: (Number) TYPE_XX constant defining the UI component.
 * - code: (String) MENU_CODE constant (API JSON key).
 * - form: (Number) Data format (FORMAT.INT, STRING, FLOAT).
 * - list: (Array, Optional) Options for TYPE_SEL (Select) or TYPE_BAR (Color).
 * - min/max: (Number, Optional) Constraints for TYPE_INP (Input) or TYPE_RAN (Range).
 * - ref: (String, Optional) MENU_CODE of a dependency field (for conditional hiding).
 * - highlight: (Boolean, Optional) If true, renders with a prominent style.
 *
 * [ACTION SETTING ITEMS]
 * Items like 'Events' or 'Actions' often use TYPE_BTN (Button) or TYPE_LST (List).
 * - ACTION_LIST: Used for custom Modbus/HMI action sequences.
 * - EVENT: Used for standard AI/DI/DO port triggers.
 *
 * [한국어]
 * 템플릿 배열(예: ANALOG_INPUT_TEMPLATE) 내부 객체의 스키마를 정의합니다.
 * - name: (String) 라벨용 번역 키 (예: 'SETTING.str02').
 * - type: (Number) UI 컴포넌트를 정의하는 TYPE_XX 상수.
 * - code: (String) MENU_CODE 상수 (API JSON 키).
 * - form: (Number) 데이터 형식 (FORMAT.INT, STRING, FLOAT).
 * - list: (Array, 선택사항) TYPE_SEL(선택) 또는 TYPE_BAR(색상)를 위한 옵션 목록.
 * - min/max: (Number, 선택사항) TYPE_INP(입력) 또는 TYPE_RAN(범위)을 위한 제약 조건.
 * - ref: (String, 선택사항) 의존성 필드의 MENU_CODE (조건부 숨김용).
 * - highlight: (Boolean, 선택사항) true인 경우 강조된 스타일로 렌더링됩니다.
 *
 * [액션 설정 항목]
 * '이벤트' 또는 '액션'과 같은 항목은 주로 TYPE_BTN (버튼) 또는 TYPE_LST (목록)을 사용합니다.
 * - ACTION_LIST: 커스텀 모드버스/HMI 액션 시퀀스에 사용됩니다.
 * - EVENT: 표준 AI/DI/DO 포트 트리거에 사용됩니다.
 */
export const MENU_CODE = {
  DEMO: 'demo', // UI Widget preview
  ENAB: 'ui_display', // Port Enable/Disable flag
  AIMODE: 'ai_mode', // Analog Input Mode (0~5V, 4~20mA)
  PORTDESC: 'port_desc', // Port Name/Description
  WIDGET: 'ui_widget', // Selected Widget Type ID
  UNIT: 'unit', // Display Unit (e.g. °C, %)
  COLOR: 'ui_color', // Selected UI Color HEX
  DISPLAYMIN: 'display_min', // Dashboard Chart/Gauge Min Scale
  DISPLAYMAX: 'display_max', // Dashboard Chart/Gauge Max Scale
  PORTMIN: 'port_min', // Hardware Calibration Min
  PORTMAX: 'port_max', // Hardware Calibration Max
  EVENT: 'events', // Event Triggers List
  FORMAT: 'dec_place', // Decimal Places / Number Formatting
  SCHEDULE: 'schedules', // Cron-like Schedules List
  SAFEMODE: 'safe_mode', // Security/Safe mode fallback settings
  RELAYMODE: 'relay_mode', // Relay Mode (Normal, Pulse, etc.)
  BUTTONS: 'commands', // Web Control Buttons List
  COUNTTYPE: 'count_type', // DI Counter Edge Type (Rising/Falling)
  COUNTDIR: 'count_dir', // DI Counter Direction (Up/Down)
  COUNTDEF: 'count_def', // DI Counter Reset/Default Value
  VALIDTIME: 'valid_time', // Debounce / Valid Time in ms
  DEVDESC: 'dev_desc', // General - Device Name
  DEVMACADDR: 'mac_addr', // General - Mac Address (Read-only usually)
  DEVMODEL: 'dev_model', // General - Hardware Model
  /*
    Log specific menu codes...
    DEVREGLOG: 'dev_trigger_registration',
    // ...
  */
  DEV_NOTICE: 'dev_notice',
  ACTION_LIST: 'action_list',

  // Modbus Configuration
  MBUS_MODE: 'modbus_mode',
  MBUS_TIMEOUT: 'modbus_timeout',
  MBUS_IFGAP: 'modbus_interframe_gap',

  // Serial Configuration
  BAUD_RATE: 'serial_baudrate',
  PARITY: 'serial_parity',
  DATA_BIT: 'serial_data_bit',
  STOP_BIT: 'serial_stop_bit',

  // Slave Configuration
  MBUS_SLAVES: 'ports', // Maps the entire Modbus slave array
};

/**
 * [DEV] FIELD DEPENDENCIES & UI LOGIC (UI DEPENDENCY SYSTEM)
 *
 * [English]
 * The UI Engine supports 'ref' and 'highlight' properties for basic field dependency.
 *
 * - ref: Links one field to another within the same port tab.
 *        This is read by `DeviceSettingMenu.vue` to dynamically apply the `hide-setting` CSS class.
 *        Example: Hiding the 'demo' widget if the 'enab' (Enable) toggle is turned off.
 *          { code: MENU_CODE.DEMO, ref: MENU_CODE.ENAB }
 *          Then, in DeviceSettingMenu.vue -> `v-bind:class="{ 'hide-setting': !port[tab.ref] }"`
 *
 * - highlight: If true, denotes a primary setting in the UI.
 *
 * [한국어]
 * UI 엔진은 'ref' 및 'highlight' 속성을 통해 기본적인 필드 의존성(조건부 렌더링)을 지원합니다.
 *
 * - ref: 한 필드를 동일한 포트 탭 내의 다른 필드와 연결합니다.
 *        `DeviceSettingMenu.vue`에서 이 값을 읽어 `hide-setting` CSS 클래스를 동적으로 적용합니다.
 *        예시: 'enab' (활성화) 토글이 꺼져 있으면 'demo' 위젯 표시 안 함.
 *          { code: MENU_CODE.DEMO, ref: MENU_CODE.ENAB }
 *          그리고 DeviceSettingMenu.vue 내에서 -> `v-bind:class="{ 'hide-setting': !port[tab.ref] }"`
 *
 * - highlight: true인 경우 UI에서 주요 설정으로 표시됩니다.
 */

/* --- FIELD FACTORIES --- */
/**
 * [DEV] Internal Helper: Core field factory function.
 * 핵심 필드 팩토리 함수입니다.
 *
 * [English]
 * - name: Translation key (e.g., 'SETTING.str02')
 * - type: TYPE_XX constant
 * - code: MENU_CODE constant (backend key)
 * - extra: Additional props (e.g., { list: [...], min: 0, max: 100 })
 *
 * [HOW-TO: Add a Select Box]
 * 1. Define options: `const opts = [{ label: 'On', value: 1 }, { label: 'Off', value: 0 }];`
 * 2. Use TYPE_SEL: `createField('MY.LABEL', TYPE_SEL, 'my_key', { list: opts });`
 *
 * [한국어]
 * - name: 번역 키 (예: 'SETTING.str02')
 * - type: TYPE_XX 상수
 * - code: MENU_CODE 상수 (백엔드 키)
 * - extra: 추가 속성 (예: { list: [...], min: 0, max: 100 })
 *
 * [방법: 셀렉트 박스 추가]
 * 1. 옵션 정의: `const opts = [{ label: '켜짐', value: 1 }, { label: '꺼짐', value: 0 }];`
 * 2. TYPE_SEL 사용: `createField('MY.LABEL', TYPE_SEL, 'my_key', { list: opts });`
 */
const createField = (name, type, code, extra = {}) => ({
  name,
  type,
  code,
  ...extra,
});

const createEnableField = () =>
  createField('SETTING.str02', TYPE_SEL, MENU_CODE.ENAB, {
    list: [
      { label: 'SETTING.str15', value: 1 },
      { label: 'SETTING.str16', value: 0 },
    ],
    form: FORMAT.INT,
  });

const createDescField = () =>
  createField('SETTING.str04', TYPE_INP, MENU_CODE.PORTDESC, {
    max_len: MAX_STRING_LEN,
    form: FORMAT.STRING,
  });

const createWidgetField = (list) =>
  createField('SETTING.str03', TYPE_SEL, MENU_CODE.WIDGET, {
    list,
    form: FORMAT.INT,
  });

const createColorField = (list) =>
  createField('SETTING.str06', TYPE_BAR, MENU_CODE.COLOR, {
    list,
    form: FORMAT.STRING,
  });

const createDemoField = () =>
  createField('SETTING.str52', TYPE_WGT, MENU_CODE.DEMO, {
    ref: MENU_CODE.ENAB,
    custom_class: 'demo-item',
  });

/**
 * [DEV] Internal Helper: Creates a Range (Min/Max) input UI unit.
 * 범위(최소/최대) 입력 UI 단위를 생성합니다.
 */
const createRangeFields = (name, minCode, maxCode) => [
  {
    name,
    type: TYPE_RAN,
    code: minCode,
    ref: maxCode,
    form: FORMAT.FLOAT,
    highlight: false,
  },
  { code: maxCode },
];

const createEventBtn = () =>
  createField('SETTING.str09', TYPE_BTN, MENU_CODE.EVENT, {
    custom_class: 'single-row-item',
  });

/**
 * [DEV] Top-Level Categories (CATEGORY)
 *
 * [English]
 * Defines the Sidebar menus in the Device Setting modal.
 * To add a new Category:
 * 1. Add a new object to this array.
 * 2. Define an 'id' and 'label' (i18n key).
 * 3. List the 'MENU_CODE' values that belong to this category in the 'list' array.
 *
 * [한국어]
 * 장치 설정 모달의 사이드바 메뉴를 정의합니다.
 * 카테고리 추가 방법:
 * 1. 이 배열에 새 객체를 추가합니다.
 * 2. 'id'와 'label'(다국어 키)을 정의합니다.
 * 3. 'list' 배열에 이 카테고리에 속할 'MENU_CODE' 값들을 나열합니다.
 */
export const CATEGORY = [
  {
    id: 'cat_gen',
    label: 'SETTING.str36',
    list: [
      MENU_CODE.DEVDESC,
      MENU_CODE.DEVMACADDR,
      MENU_CODE.DEVMODEL,
      MENU_CODE.SEPARATOR,
      MENU_CODE.DEVREGLOG,
      MENU_CODE.DEVCONNLOG,
      MENU_CODE.DEVSETTLOG,
      MENU_CODE.DEVWEBLOG,
      MENU_CODE.DEVEVTLOG,
      MENU_CODE.DEVSCHLOG,
      MENU_CODE.GGLOG,
    ],
    icon: ICON_CAT_GENERAL,
  },
  {
    id: 'cat_dev',
    label: 'SETTING.str37',
    list: [
      MENU_CODE.AIMODE,
      MENU_CODE.PORTDESC,
      MENU_CODE.PORTMIN,
      MENU_CODE.SAFEMODE,
      MENU_CODE.VALIDTIME,
      MENU_CODE.COUNTTYPE,
      MENU_CODE.COUNTDIR,
      MENU_CODE.COUNTDEF,
      MENU_CODE.DEV_NOTICE,
    ],
    icon: ICON_CAT_DEVICE,
  },
  {
    id: 'cat_wgt',
    label: 'SETTING.str03',
    list: [
      MENU_CODE.ENAB,
      MENU_CODE.WIDGET,
      MENU_CODE.UNIT,
      MENU_CODE.COLOR,
      MENU_CODE.DISPLAYMIN,
      MENU_CODE.FORMAT,
      MENU_CODE.COLOR,
      MENU_CODE.BUTTONS,
      MENU_CODE.RELAYMODE,
      MENU_CODE.DEMO,
    ],
    icon: ICON_CAT_WIDGET,
  },
  {
    id: 'cat_evt',
    label: 'SETTING.str09',
    list: [MENU_CODE.EVENT],
    icon: ICON_CAT_EVENT,
  },
  {
    id: 'cat_act',
    label: 'SETTING.str99',
    list: [MENU_CODE.ACTION_LIST],
    icon: ICON_CAT_ACTION,
  },
  {
    id: 'cat_sch',
    label: 'SETTING.str10',
    list: [MENU_CODE.SCHEDULE],
    icon: ICON_CAT_SCHEDULE,
  },
  {
    id: 'cat_ser',
    label: 'SETTING.serial',
    display: 'SETTING.str37',
    list: [
      MENU_CODE.BAUD_RATE,
      MENU_CODE.PARITY,
      MENU_CODE.DATA_BIT,
      MENU_CODE.STOP_BIT,
    ],
    icon: ICON_CAT_SERIAL,
  },
  {
    id: 'cat_mdb',
    label: 'SETTING.mbus',
    display: 'SETTING.str37',
    list: [MENU_CODE.MBUS_MODE, MENU_CODE.MBUS_TIMEOUT, MENU_CODE.MBUS_IFGAP],
    icon: ICON_CAT_MODBUS,
  },
  {
    id: 'cat_qry',
    label: 'SETTING.queries',
    list: [MENU_CODE.MBUS_SLAVES],
    icon: ICON_CAT_QUERY,
  },
];

const ANALOG_INPUT_TEMPLATE = [
  createEnableField(),
  createDescField(),
  {
    name: 'SETTING.str41',
    type: TYPE_TXT,
    list: [
      { label: '0 ~ 5V', value: 0 },
      { label: '4 ~ 20mA', value: 1 },
      { label: '0 ~ 20mA', value: 2 },
    ],
    code: MENU_CODE.AIMODE,
  },
  ...createRangeFields('SETTING.str86', MENU_CODE.PORTMIN, MENU_CODE.PORTMAX),
  createWidgetField([
    { label: 'SETTING.str23', value: WGT_AI_TEXT },
    { label: 'SETTING.str24', value: WGT_AI_GAUGE },
    { label: 'SETTING.str26', value: WGT_AI_THERMO },
    { label: 'SETTING.str27', value: WGT_AI_DROPLET },
    { label: 'SETTING.str28', value: WGT_AI_HBAR },
    { label: 'SETTING.str29', value: WGT_AI_VBAR },
    { label: 'SETTING.str30', value: WGT_AI_CBAR },
    { label: 'SETTING.str25', value: WGT_AI_CHART },
  ]),
  {
    name: 'SETTING.str05',
    type: TYPE_CBB,
    code: MENU_CODE.UNIT,
    max_len: MAX_UNIT_STR_LEN,
    form: FORMAT.STRING,
    list: [
      { label: 'Ω', value: 'Ω' },
      { label: '%', value: '%' },
      { label: '‰', value: '‰' },
      { label: '°C', value: '°C' },
      { label: '°F', value: '°F' },
    ],
  },
  createColorField(COLOR_LIST),
  ...createRangeFields(
    'SETTING.str87',
    MENU_CODE.DISPLAYMIN,
    MENU_CODE.DISPLAYMAX
  ),
  {
    name: 'SETTING.str44',
    type: TYPE_INP,
    code: MENU_CODE.FORMAT,
    form: FORMAT.INT,
    min: MIN_DEC_PLACE,
    max: MAX_DEC_PLACE,
  },
  createEventBtn(),
  createDemoField(),
];

const DIGITAL_INPUT_TEMPLATE = [
  createEnableField(),
  createDescField(),
  {
    name: 'SETTING.str58',
    type: TYPE_INP,
    code: MENU_CODE.VALIDTIME,
    form: FORMAT.INT,
    odev: true,
    min: 100,
    max: 30000,
  },
  {
    name: 'SETTING.str53',
    type: TYPE_SEL,
    list: [
      { label: 'SETTING.str54', value: 1 },
      { label: 'SETTING.str55', value: 2 },
      { label: 'SETTING.str56', value: 3 },
    ],
    code: MENU_CODE.COUNTTYPE,
    odev: true,
    form: FORMAT.INT,
  },
  {
    name: 'SETTING.str57',
    type: TYPE_SEL,
    list: [
      { label: 'SETTING.str81', value: 0 },
      { label: 'SETTING.str82', value: 1 },
    ],
    code: MENU_CODE.COUNTDIR,
    odev: true,
    form: FORMAT.INT,
  },
  {
    name: 'SETTING.str88',
    type: TYPE_INP,
    code: MENU_CODE.COUNTDEF,
    form: FORMAT.INT,
    odev: true,
    min: MIN_INT32,
    max: MAX_INT32,
  },
  {
    name: 'SETTING.str94',
    type: TYPE_SPR,
    code: MENU_CODE.DEV_NOTICE,
    custom_class: 'notice-row-item',
  },
  createWidgetField([
    { label: 'SETTING.str31', value: WGT_DI_DLED },
    { label: 'SETTING.str62', value: WGT_DI_COUNTER },
    { label: 'SETTING.str25', value: WGT_DI_CHART },
  ]),
  createColorField(COLOR_LIST),
  createEventBtn(),
  createDemoField(),
];

const DIGITAL_OUTPUT_TEMPLATE = [
  createEnableField(),
  createDescField(),
  {
    name: 'SETTING.str59',
    type: TYPE_TXT,
    list: [
      { label: 'SETTING.str61', value: 0 },
      { label: 'SETTING.str60', value: 1 },
    ],
    code: MENU_CODE.SAFEMODE,
  },
  /* Temporarily disabled
  {
    name: 'SETTING.str83',
    type: TYPE_SEL,
    list: [
      { label: 'SETTING.str84', value: 0 },
      { label: 'SETTING.str85', value: 1 },
    ],
    code: MENU_CODE.RELAYMODE,
    form: FORMAT.INT,
  },
  */
  {
    name: 'SETTING.str03',
    type: TYPE_SEL,
    list: [
      { label: 'SETTING.str31', value: WGT_DO_PLED },
      { label: 'SETTING.str89', value: WGT_DO_SCHL },
      { label: 'SETTING.str25', value: WGT_DO_CHART },
      { label: 'SETTING.str104', value: WGT_DO_AMY },
    ],
    code: MENU_CODE.WIDGET,
    form: FORMAT.INT,
  },
  {
    name: 'SETTING.str06',
    type: TYPE_BAR,
    list: COLOR_LIST,
    code: MENU_CODE.COLOR,
    form: FORMAT.STRING,
  },
  {
    name: 'SETTING.str67',
    custom_class: 'button-row-item',
    type: TYPE_CMD,
    code: MENU_CODE.BUTTONS,
    ref: MENU_CODE.RELAYMODE,
  },
  {
    type: TYPE_BTN,
    code: MENU_CODE.EVENT,
    custom_class: 'single-row-item',
  },
  {
    type: TYPE_BTN,
    code: MENU_CODE.SCHEDULE,
    custom_class: 'single-row-item',
    ref: MENU_CODE.RELAYMODE,
  },
  createDemoField(),
  {
    type: TYPE_LST,
    code: MENU_CODE.ACTION_LIST,
    custom_class: 'single-row-item action-row-item',
  },
];

const ANALOG_THERMO_TEMPLATE = [
  {
    name: 'SETTING.str02',
    type: TYPE_SEL,
    list: [
      { label: 'SETTING.str15', value: 1 },
      { label: 'SETTING.str16', value: 0 },
    ],
    code: MENU_CODE.ENAB,
    form: FORMAT.INT,
  },
  {
    name: 'SETTING.str04',
    type: TYPE_INP,
    code: MENU_CODE.PORTDESC,
    max_len: MAX_STRING_LEN,
    form: FORMAT.STRING,
  },
  {
    name: 'SETTING.str03',
    type: TYPE_SEL,
    list: [
      { label: 'SETTING.str23', value: WGT_AI_TEXT },
      { label: 'SETTING.str24', value: WGT_AI_GAUGE },
      { label: 'SETTING.str26', value: WGT_AI_THERMO },
      { label: 'SETTING.str07', value: WGT_AI_7SEG },
      { label: 'SETTING.str28', value: WGT_AI_HBAR },
      { label: 'SETTING.str29', value: WGT_AI_VBAR },
      { label: 'SETTING.str30', value: WGT_AI_CBAR },
      { label: 'SETTING.str25', value: WGT_AI_CHART },
    ],
    code: MENU_CODE.WIDGET,
    form: FORMAT.INT,
  },
  {
    name: 'SETTING.str103',
    type: TYPE_TXT,
    code: MENU_CODE.UNIT,
    list: [
      { label: '°C', value: '°C' },
      { label: '°F', value: '°F' },
    ],
  },
  {
    name: 'SETTING.str06',
    type: TYPE_BAR,
    list: COLOR_LIST,
    code: MENU_CODE.COLOR,
    form: FORMAT.STRING,
  },
  {
    name: 'SETTING.str87',
    type: TYPE_RAN,
    code: MENU_CODE.DISPLAYMIN,
    ref: MENU_CODE.DISPLAYMAX,
    form: FORMAT.FLOAT,
    highlight: false,
  },
  {
    code: MENU_CODE.DISPLAYMAX,
  },
  {
    name: 'SETTING.str09',
    type: TYPE_BTN,
    code: MENU_CODE.EVENT,
    custom_class: 'single-row-item',
  },
  {
    name: 'SETTING.str52',
    type: TYPE_WGT,
    code: MENU_CODE.DEMO,
    ref: MENU_CODE.ENAB,
    custom_class: 'demo-item',
  },
];

const GENERAL_INFO_TAB = [
  {
    type: TYPE_TAB,
    name: 'SETTING.str51',
    tab: 'deviceinfo',
  },
  /*,
    {
        type: TYPE_TAB,
        name: 'SETTING.str66',
        tab: 'devicelog'
    } 
    */
];

const MODBUS_TAB = [
  {
    type: TYPE_TAB,
    name: 'SETTING.queryList',
    tab: 'modbus',
  },
  {
    type: TYPE_TAB,
    name: 'SETTING.serial',
    tab: 'serial',
  },
  {
    type: TYPE_TAB,
    name: 'SETTING.queries',
    tab: 'query',
  },
];

const MODBUS_INFO_TEMPLATE = [
  {
    name: 'SETTING.mbusMode',
    type: TYPE_SEL,
    code: MENU_CODE.MBUS_MODE,
    list: [
      { value: 0, label: 'Modbus RTU' },
      { value: 1, label: 'Modbus ASCII' },
    ],
    form: FORMAT.INT,
  },
  {
    name: 'SETTING.resTimeout',
    type: TYPE_INP,
    code: MENU_CODE.MBUS_TIMEOUT,
    form: FORMAT.INT,
    min: 200,
    max: 3000,
  },
  {
    name: 'SETTING.interframeGap',
    type: TYPE_INP,
    code: MENU_CODE.MBUS_IFGAP,
    form: FORMAT.INT,
    min: MIN_INTERFRAME_GAP,
    max: MAX_INTERFRAME_GAP,
  },
  {
    name: 'SETTING.baudRate',
    type: TYPE_SEL,
    code: MENU_CODE.BAUD_RATE,
    list: BAUD_RATE,
    form: FORMAT.INT,
  },
  {
    name: 'SETTING.parity',
    type: TYPE_SEL,
    code: MENU_CODE.PARITY,
    list: PARITY,
    form: FORMAT.INT,
  },
  {
    name: 'SETTING.dataBits',
    type: TYPE_SEL,
    code: MENU_CODE.DATA_BIT,
    list: DATA_BIT,
    form: FORMAT.INT,
  },
  {
    name: 'SETTING.stopBits',
    type: TYPE_SEL,
    code: MENU_CODE.STOP_BIT,
    list: STOP_BIT,
    form: FORMAT.INT,
  },
  {
    type: TYPE_SLV,
    code: MENU_CODE.MBUS_SLAVES,
    custom_class: 'single-row-item slave-row-item',
  },
];

const DEVICE_INFO_TEMPLATE_COMMON = [
  {
    name: 'SETTING.str50',
    type: TYPE_TXT,
    code: MENU_CODE.DEVMODEL,
  },
  {
    name: 'SETTING.str49',
    type: TYPE_TXT,
    code: MENU_CODE.DEVMACADDR,
  },
  {
    name: 'SETTING.str01',
    type: TYPE_INP,
    code: MENU_CODE.DEVDESC,
    max_len: MAX_STRING_LEN,
    form: FORMAT.STRING,
  },
];

function createGeneralTab() {
  return GENERAL_INFO_TAB.concat(
    ...createTabData([GENERAL_INFO_TAB[0].tab], DEVICE_INFO_TEMPLATE_COMMON)
  );
}

function createModbusTab() {
  return MODBUS_TAB.concat(
    ...createTabData([MODBUS_TAB[0].tab], MODBUS_INFO_TEMPLATE)
  );
}

/**
 * [DEV] TAB GENERATION GUIDES
 * Most port settings are organized into tabs (e.g., Ai0, Ai1).
 * 대부분의 포트 설정은 탭으로 구성됩니다 (예: Ai0, Ai1).
 *
 * To add a new Port Tab type:
 * 1. Create a factory function (e.g., createMyNewTab).
 * 2. Use TYPE_TAB to define the layout container.
 * 3. Use createTabData() to map a template to those tabs.
 */
/**
 * [DEV] Internal Helper: Creates specific tabs for different port types.
 * 다양한 포트 유형에 대한 특정 탭을 생성합니다.
 */
function createThermoTab(numOfPorts) {
  const template = {
    type: TYPE_TAB,
  };
  const tabData = [];
  for (let i = 0; i < numOfPorts; i++) {
    tabData.push(template);
  }
  return tabData.map((t, index) => ({
    ...t,
    name: 'Ts' + index,
    tab: 'ts' + index,
  }));
}

function createAITab(numOfPorts) {
  const template = {
    type: TYPE_TAB,
  };
  const tabData = [];
  for (let i = 0; i < numOfPorts; i++) {
    tabData.push(template);
  }
  return tabData.map((t, index) => ({
    ...t,
    name: 'Ai' + index,
    tab: 'ai' + index,
  }));
}

function createDITab(numOfPorts) {
  const template = {
    type: TYPE_TAB,
  };
  const tabData = [];
  for (let i = 0; i < numOfPorts; i++) {
    tabData.push(template);
  }
  return tabData.map((t, index) => ({
    ...t,
    name: 'Di' + index,
    tab: 'di' + index,
  }));
}

function createDOTab(numOfPorts) {
  const template = {
    type: TYPE_TAB,
  };
  const tabData = [];
  for (let i = 0; i < numOfPorts; i++) {
    tabData.push(template);
  }
  return tabData.map((t, index) => ({
    ...t,
    name: 'Do' + index,
    tab: 'do' + index,
  }));
}

function createSIG5431Template() {
  const tabs = createAITab(4);
  const list = tabs.map((tab) => tab.tab);
  return createGeneralTab().concat(tabs.concat(...createAnalogInputPort(list)));
}

function createSIG5441Template() {
  const tabs = createDITab(4);
  const list = tabs.map((tab) => tab.tab);
  return createGeneralTab().concat(
    tabs.concat(...createDigitalInputPort(list))
  );
}

function createSIG5451Template() {
  const tabs = createDOTab(2);
  const list = tabs.map((tab) => tab.tab);
  return createGeneralTab().concat(
    tabs.concat(...createDigitalOutputPort(list))
  );
}

function createSIG5601Template() {
  const tabAI = createAITab(4);
  const tabDI = createDITab(4);
  const tabDO = createDOTab(4);
  const tabs = tabAI.concat(tabDI).concat(tabDO);
  const listAI = createAnalogInputPort(tabAI.map((tab) => tab.tab));
  const listDI = createDigitalInputPort(tabDI.map((tab) => tab.tab));
  const listDO = createDigitalOutputPort(tabDO.map((tab) => tab.tab));
  return createGeneralTab().concat(
    tabs
      .concat(...listAI)
      .concat(...listDI)
      .concat(...listDO)
  );
}

function createSMGTemplate() {
  return createGeneralTab().concat(createModbusTab());
}

function createSIGThermoTemplate() {
  const tabs = createThermoTab(1);
  const list = tabs.map((tab) => tab.tab);
  return createGeneralTab().concat(
    tabs.concat(...createAnalogThermoPort(list))
  );
}

/**
 * [DEV] Internal Helper: Creates a single port data item.
 * 단일 포트 데이터 항목을 생성합니다.
 */
function createTabData(numOfTabs, template) {
  const tabData = [];
  for (let i = 0; i < numOfTabs.length; i++) {
    tabData.push(template);
  }
  return tabData.map((t, index) =>
    t.map((item) => ({ ...item, parent: numOfTabs[index] }))
  );
}

/**
 * [DEV] Internal Helper: Port specific template creators.
 * 포트별 템플릿 생성기 모음입니다.
 */
function createAnalogInputPort(numOfTabs) {
  return createTabData(numOfTabs, ANALOG_INPUT_TEMPLATE);
}

function createDigitalInputPort(numOfTabs) {
  return createTabData(numOfTabs, DIGITAL_INPUT_TEMPLATE);
}

function createDigitalOutputPort(numOfTabs) {
  return createTabData(numOfTabs, DIGITAL_OUTPUT_TEMPLATE);
}

function createAnalogThermoPort(numOfTabs) {
  return createTabData(numOfTabs, ANALOG_THERMO_TEMPLATE);
}

/**
 * [DEV] Internal Helper: populates the UI template with current data.
 * UI 템플릿에 현재 장치 데이터를 채워 화면에 표시할 수 있는 상태로 만듭니다.
 */
export function getDeviceValue(template, vals = {}) {
  template.forEach((el) => {
    if (el.parent) {
      if (
        GENERAL_INFO_TAB.map((el) => el.tab).indexOf(el.parent) === -1 &&
        MODBUS_TAB.map((el) => el.tab).indexOf(el.parent) === -1
      ) {
        const portObject = vals.ports.find(
          (port) => port.port_name === el.parent
        );
        if (portObject == null) return;

        if (el.code != null) {
          if (portObject[el.code] instanceof Array) {
            el.value = portObject[el.code].slice(0);
          } else {
            el.value = portObject[el.code];
          }
        }
      } else {
        if (el.code != null) {
          el.value = vals[el.code];
        }
      }
    }
  });
  return template;
}

/**
 * [DEV] Internal Helper: Updates the local data object with values from the UI template.
 * UI 템플릿의 값을 로컬 데이터 객체에 업데이트합니다.
 */
export function setDeviceValue(template, portsData) {
  template.forEach((el) => {
    if (el.parent) {
      if (
        GENERAL_INFO_TAB.map((el) => el.tab).indexOf(el.parent) === -1 &&
        MODBUS_TAB.map((el) => el.tab).indexOf(el.parent) === -1
      ) {
        const portObject = portsData.ports.find(
          (port) => port.port_name === el.parent
        );
        portObject[el.code] = el.value;
      } else {
        portsData[el.code] = el.value;
      }
    }
  });
  return portsData;
}

/**
 * [DEV] Internal Helper: Filters and prepares schedule settings for comparison.
 * 비교를 위해 스케줄 설정을 필터링하고 준비합니다.
 */
function getScheduleSetting(settings, referenceSetting) {
  return settings
    .filter((schedule) => {
      switch (schedule.action) {
        case STS.ADD:
        case STS.DELETE:
          return true;
        case STS.UPDATE: {
          const ref = referenceSetting.find(
            (s) => s.schedule_id === schedule.schedule_id
          );
          return (
            ref &&
            (ref.repeated !== schedule.repeated ||
              JSON.stringify(ref.time_dayofweek.sort()) !==
                JSON.stringify(schedule.time_dayofweek.sort()) ||
              ref.time_hours !== schedule.time_hours ||
              ref.time_minutes !== schedule.time_minutes ||
              getBoolean(ref.schedule_enabled) !==
                getBoolean(schedule.schedule_enabled) ||
              ref.command !== schedule.command ||
              JSON.stringify(ref.event_action.sort()) !==
                JSON.stringify(schedule.event_action.sort()))
          );
        }
      }
    })
    .map((sch) => {
      if (sch.action === STS.ADD) {
        return {
          action: sch.action,
          repeated: sch.repeated,
          time_dayofweek: sch.time_dayofweek,
          time_hours: sch.time_hours,
          time_minutes: sch.time_minutes,
          command: sch.command,
          event_action: sch.event_action,
        };
      } else if (sch.action === STS.DELETE) {
        return {
          action: sch.action,
          schedule_id: sch.schedule_id,
        };
      } else if (sch.action === STS.UPDATE) {
        return {
          action: sch.action,
          schedule_id: sch.schedule_id,
          repeated: sch.repeated,
          time_dayofweek: sch.time_dayofweek,
          time_hours: sch.time_hours,
          time_minutes: sch.time_minutes,
          command: sch.command,
          event_action: sch.event_action,
          schedule_enabled: getBoolean(sch.schedule_enabled),
        };
      }
    });
}

/**
 * [DEV] Internal Helper: Filters and prepares Modbus/HMI slave settings.
 * 모드버스/HMI 슬레이브 설정을 필터링하고 준비합니다.
 */
function getSlaveSetting(settings, referenceSetting) {
  return settings
    .filter((slave) => {
      switch (slave.action) {
        case STS.ADD:
        case STS.DELETE:
          return true;
        case STS.UPDATE: {
          const ref = referenceSetting.find((s) => s.port_id === slave.port_id);
          return (
            ref &&
            (ref.slave_addr !== slave.slave_addr ||
              (ref.query_type === 'MB' &&
                (ref.req_func !== slave.req_func ||
                  ref.req_addr !== slave.req_addr)) ||
              (ref.query_type === 'HMI' && ref.hmi_addr !== slave.hmi_addr) ||
              ref.rep_offset !== slave.rep_offset ||
              ref.rep_scale !== slave.rep_scale ||
              ref.slave_desc !== slave.slave_desc ||
              ref.dec_place !== slave.dec_place ||
              ref.data_type !== slave.data_type ||
              ref.byte_swap !== slave.byte_swap ||
              ref.word_swap !== slave.word_swap ||
              ref.dword_swap !== slave.dword_swap ||
              ref.port_offset !== slave.port_offset ||
              ref.port_quantity !== slave.port_quantity ||
              getBoolean(ref.slave_enabled) !==
                getBoolean(slave.slave_enabled) ||
              (typeof ref.widget === 'string'
                ? ref.widget
                : JSON.stringify(ref.widget)) !==
                (typeof slave.widget === 'string'
                  ? slave.widget
                  : JSON.stringify(slave.widget)))
          );
        }
      }
    })
    .map((slv) => {
      if (slv.action === STS.ADD) {
        return {
          action: slv.action,
          slave_desc: slv.slave_desc,
          slave_addr: slv.slave_addr,
          rep_offset: slv.rep_offset,
          rep_scale: slv.rep_scale,
          query_type: slv.query_type,
          ...(slv.query_type === 'MB'
            ? {
                req_func: slv.req_func,
                req_addr: slv.req_addr,
              }
            : {
                hmi_addr: slv.hmi_addr,
              }),
          byte_swap: slv.byte_swap,
          word_swap: slv.word_swap,
          dword_swap: slv.dword_swap,
          port_offset: slv.port_offset,
          port_quantity: slv.port_quantity,
          data_type: slv.data_type,
          dec_place: slv.dec_place,
          // req_data: slv.req_data,
          widget:
            typeof slv.widget === 'string'
              ? slv.widget
              : JSON.stringify(slv.widget),
        };
      } else if (slv.action === STS.DELETE) {
        return {
          action: slv.action,
          port_id: slv.port_id,
          query_type: slv.query_type,
        };
      } else if (slv.action === STS.UPDATE) {
        const ref = referenceSetting.find((s) => s.port_id === slv.port_id);

        return {
          action: slv.action,
          port_id: slv.port_id,
          query_type: slv.query_type,
          slave_desc: slv.slave_desc,
          slave_addr: slv.slave_addr,
          rep_offset: slv.rep_offset,
          rep_scale: slv.rep_scale,
          dec_place: slv.dec_place,
          events: getEventSetting(slv.events, true, ref.events),

          ...(slv.query_type === 'MB'
            ? {
                req_func: slv.req_func,
                req_addr: slv.req_addr,
              }
            : {
                hmi_addr: slv.hmi_addr,
              }),
          byte_swap: slv.byte_swap,
          word_swap: slv.word_swap,
          dword_swap: slv.dword_swap,
          port_offset: slv.port_offset,
          port_quantity: slv.port_quantity,
          data_type: slv.data_type,
          widget:
            typeof slv.widget === 'string'
              ? slv.widget
              : JSON.stringify(slv.widget),
          slave_enabled: getBoolean(slv.slave_enabled),
        };
      }
    });
}

/**
 * [DEV] Internal Helper: Filters and prepares control actions for an event.
 * 이벤트에 대한 제어 액션을 필터링하고 준비합니다.
 */
function getActionSetting(actionList, referenceSetting) {
  return actionList
    .filter((action) => {
      switch (action.action) {
        case STS.ADD:
        case STS.DELETE:
          return true;
        case STS.UPDATE: {
          const ref =
            referenceSetting &&
            referenceSetting.ctrl_actions &&
            referenceSetting.ctrl_actions.find(
              (a) => a.ctrl_action_id === action.ctrl_action_id
            );
          return (
            ref &&
            (ref.port_id !== action.port_id || ref.command !== action.command)
          );
        }
      }
    })
    .map((evt) => {
      if (evt.action === STS.ADD) {
        if (!referenceSetting) {
          return {
            port_id: evt.port_id,
            command: evt.command,
          };
        } else {
          return {
            action: evt.action,
            port_id: evt.port_id,
            command: evt.command,
          };
        }
      } else if (evt.action === STS.DELETE) {
        return {
          action: evt.action,
          ctrl_action_id: evt.ctrl_action_id,
        };
      } else if (evt.action === STS.UPDATE) {
        return {
          action: evt.action,
          port_id: evt.port_id,
          command: evt.command,
          ctrl_action_id: evt.ctrl_action_id,
        };
      }
    });
}

/**
 * [DEV] Internal Helper: Filters and prepares event triggers for a port.
 * 포트에 대한 이벤트 트리거 설정을 필터링하고 준비합니다.
 */
function getEventSetting(settings, isAnalog, referenceSetting) {
  return settings
    .filter((event) => {
      switch (event.action) {
        case STS.ADD:
        case STS.DELETE:
          return true;
        case STS.UPDATE: {
          const ref = referenceSetting.find(
            (e) => e.event_id === event.event_id
          );
          return (
            ref &&
            (ref.event_condition !== event.event_condition ||
              ref.event_desc !== event.event_desc ||
              ref.event_value !== event.event_value ||
              ref.event_noise !== event.event_noise ||
              getBoolean(ref.event_enabled) !==
                getBoolean(event.event_enabled) ||
              JSON.stringify(ref.event_action.sort()) !==
                JSON.stringify(event.event_action.sort()) ||
              getActionSetting(event.ctrl_actions, ref).length)
          );
        }
      }
    })
    .map((evt) => {
      if (evt.action === STS.ADD) {
        return Object.assign(
          {
            action: evt.action,
            event_condition: evt.event_condition,
            event_desc: evt.event_desc, // encodeURI(evt.event_desc),
            event_value: Number(evt.event_value),
            event_action: evt.event_action,
            json_keys: evt.json_keys,
            ctrl_actions: getActionSetting(evt.ctrl_actions),
          },
          isAnalog && { event_noise: Number(evt.event_noise) }
        );
      } else if (evt.action === STS.DELETE) {
        return {
          action: evt.action,
          event_id: evt.event_id,
        };
      } else if (evt.action === STS.UPDATE) {
        const refer = referenceSetting.find((e) => e.event_id === evt.event_id);
        return Object.assign(
          {
            action: evt.action,
            event_condition: evt.event_condition,
            event_desc: evt.event_desc, // encodeURI(evt.event_desc),
            event_value: Number(evt.event_value),
            event_id: evt.event_id,
            event_enabled: getBoolean(evt.event_enabled),
            event_action: evt.event_action,
            json_keys: evt.json_keys,
            ctrl_actions: getActionSetting(evt.ctrl_actions, refer),
          },
          isAnalog && { event_noise: Number(evt.event_noise) }
        );
      }
    });
}

/* --- INTERNAL API HELPERS --- */

/**
 * [DEV] Internal Helper: Processes Control Actions (ADD/UPDATE/DELETE) for an Event.
 * 이벤트에 대한 제어 액션(추가/수정/삭제)을 처리하는 내부 헬퍼입니다.
 */
function _processCtrlActions(ctrlActions, eventId, res) {
  ctrlActions.forEach((ctrl_action) => {
    const { port_id, command, ctrl_action_id } = ctrl_action;
    switch (ctrl_action.action) {
      case STS.ADD:
        res.push({
          relative_url: `${API_PREFIX}/users/me/events/${eventId}/ctrl-actions`,
          method: 'POST',
          body: { port_id, command },
        });
        break;
      case STS.UPDATE:
        res.push({
          relative_url: `${API_PREFIX}/users/me/ctrl-actions/${ctrl_action_id}`,
          method: 'PUT',
          body: { port_id, command },
        });
        break;
      case STS.DELETE:
        res.push({
          relative_url: `${API_PREFIX}/users/me/ctrl-actions/${ctrl_action_id}`,
          method: 'DELETE',
          body: '',
        });
        break;
    }
  });
}

/**
 * [DEV] Internal Helper: Processes Events for a Port.
 * 포트에 대한 이벤트를 처리하고 API 요청 목록(res)에 추가합니다.
 * @param {Array} events - Current events in the UI.
 * @param {Object} originalPort - The original state from the backend.
 * @param {String} urlSuffix - e.g., 'ports/ai/1' or 'modbus-slaves/1'.
 * @param {Array} res - The accumulator for API command objects.
 * @param {String} typeSegment - Middle segment for UPDATE URL (e.g., 'ai', 'modbus-slaves').
 * @param {Boolean} isModbus - Whether the parent is a Modbus/HMI device.
 */
function _processEvents(
  events,
  originalPort,
  urlSuffix,
  res,
  typeSegment,
  isModbus
) {
  if (!events || !events.length) return;

  events.forEach((evt) => {
    const {
      event_condition,
      event_value,
      event_noise,
      event_desc,
      event_action,
      ctrl_actions,
      event_enabled,
      json_keys,
    } = evt;

    switch (evt.action) {
      case STS.ADD:
        res.push({
          relative_url: `${API_PREFIX}/users/me/${urlSuffix}/events`,
          method: 'POST',
          body: {
            event_condition,
            event_value,
            ...(event_noise != null && { event_noise }),
            event_desc,
            event_action,
            ctrl_actions,
            ...(!isModbus && { json_keys }),
          },
        });
        break;

      case STS.UPDATE: {
        // 1. Process nested Control Actions
        if (ctrl_actions) {
          _processCtrlActions(ctrl_actions, evt.event_id, res);
        }

        // 2. Check if the event itself changed (excluding children)
        const originalEvt = originalPort.events.find(
          (ev) => ev.event_id === evt.event_id
        );
        if (!originalEvt) break;

        for (const prop in evt) {
          if (
            Object.prototype.hasOwnProperty.call(evt, prop) &&
            ['ctrl_actions', 'action'].indexOf(prop) === -1
          ) {
            if (
              JSON.stringify(evt[prop]) !== JSON.stringify(originalEvt[prop])
            ) {
              res.push({
                relative_url: `${API_PREFIX}/users/me/events/${typeSegment}/${evt.event_id}`,
                method: 'PUT',
                body: {
                  event_condition,
                  event_value,
                  ...(event_noise != null && { event_noise }),
                  event_desc,
                  event_action,
                  event_enabled,
                  ...(!isModbus && { json_keys }),
                },
              });
              break;
            }
          }
        }
        break;
      }

      case STS.DELETE:
        res.push({
          relative_url: `${API_PREFIX}/users/me/events/${evt.event_id}`,
          method: 'DELETE',
          body: '',
        });
        break;
    }
  });
}

/**
 * [DEV] Internal Helper: Processes Schedules for a Port.
 * 포트에 대한 스케줄을 처리하고 API 요청 목록(res)에 추가합니다.
 */
function _processSchedules(schedules, originalPort, res) {
  if (!schedules || !schedules.length) return;

  const portSuffix = `ports/${originalPort.port_type.toLowerCase()}/${
    originalPort.port_id
  }`;

  schedules.forEach((sch) => {
    const {
      time_dayofweek,
      time_hours,
      time_minutes,
      repeated,
      command,
      event_action,
      schedule_enabled,
    } = sch;

    switch (sch.action) {
      case STS.ADD:
        res.push({
          relative_url: `${API_PREFIX}/users/me/${portSuffix}/schedules`,
          method: 'POST',
          body: {
            time_dayofweek,
            time_hours,
            time_minutes,
            repeated,
            command,
            event_action,
          },
        });
        break;

      case STS.UPDATE:
        {
          const originalSch = originalPort.schedules.find(
            (sc) => sc.schedule_id === sch.schedule_id
          );
          if (!originalSch) break;

          for (const prop in sch) {
            if (
              Object.prototype.hasOwnProperty.call(sch, prop) &&
              ['action'].indexOf(prop) === -1
            ) {
              if (
                JSON.stringify(sch[prop]) !== JSON.stringify(originalSch[prop])
              ) {
                res.push({
                  relative_url: `${API_PREFIX}/users/me/schedules/${sch.schedule_id}`,
                  method: 'PUT',
                  body: {
                    time_dayofweek,
                    time_hours,
                    time_minutes,
                    repeated,
                    command,
                    event_action,
                    schedule_enabled,
                  },
                });
                break;
              }
            }
          }
        }
        break;

      case STS.DELETE:
        res.push({
          relative_url: `${API_PREFIX}/users/me/schedules/${sch.schedule_id}`,
          method: 'DELETE',
          body: '',
        });
        break;
    }
  });
}

/**
 * [DEV] Internal Helper: Processes Modbus Slave Ports (Slaves/HMIs).
 * 모드버스 슬레이브 포트(Slaves/HMIs)를 처리하고 API 요청 목록(res)에 추가합니다.
 */
function _processSlaves(mbPorts, deviceId, original, res) {
  if (!mbPorts || !mbPorts.length) return;

  mbPorts.forEach((slv) => {
    const {
      slave_addr,
      slave_desc,
      slave_enabled,
      req_func,
      req_addr,
      hmi_addr,
      rep_offset,
      rep_scale,
      byte_swap,
      word_swap,
      dword_swap,
      port_offset,
      port_quantity,
      data_type,
      dec_place,
      widget,
      query_type,
    } = slv;

    const urlPath = query_type === 'MB' ? 'modbus-slaves' : 'hmis';

    switch (slv.action) {
      case STS.ADD:
        res.push({
          relative_url: `${API_PREFIX}/users/me/devices/${deviceId}/${urlPath}`,
          method: 'POST',
          body: {
            slave_addr,
            slave_desc,
            ...(query_type === 'MB' ? { req_func, req_addr } : { hmi_addr }),
            rep_offset,
            rep_scale,
            byte_swap,
            word_swap,
            dword_swap,
            port_offset,
            port_quantity,
            data_type,
            dec_place,
            widget,
          },
        });
        break;

      case STS.UPDATE:
        res.push({
          relative_url: `${API_PREFIX}/users/me/${urlPath}/${slv.port_id}`,
          method: 'PUT',
          body: {
            slave_addr,
            slave_desc,
            slave_enabled,
            ...(query_type === 'MB' ? { req_func, req_addr } : { hmi_addr }),
            rep_offset,
            rep_scale,
            byte_swap,
            word_swap,
            dword_swap,
            port_offset,
            port_quantity,
            data_type,
            dec_place,
            widget,
          },
        });
        break;

      case STS.DELETE:
        res.push({
          relative_url: `${API_PREFIX}/users/me/${urlPath}/${slv.port_id}`,
          method: 'DELETE',
          body: '',
        });
        break;
    }
  });
}

/**
 * [DEV] API Command Generator: getSettingList
 *
 * [English]
 * - Purpose: Generates the sequence of REST calls (POST, PUT, DELETE) needed to save changes.
 * - Logic: Compares the current UI state against the last saved state to determine the exact delta.
 * - Params:
 *   @param {Object} data - Current modified state (from Vuex).
 *   @param {String} savedData - JSON string of the state during the last successful save (for diffing).
 *   @param {Object} fullData - The original complete state from the backend (for context).
 * - Returns: {Array} List of API request objects.
 *
 * [한국어]
 * - 설명: API 명령어 생성기 (getSettingList):
 *         장치 설정을 저장하기 위해 필요한 REST API 호출(POST, PUT, DELETE) 목록을 생성합니다.
 * - 로직: 현재 UI 상태를 마지막으로 저장된 상태와 비교하여 변경된 차이점(delta)을 결정합니다.
 * - 파라미터:
 *   @param {Object} data - 현재 수정된 상태 (Vuex에서 가져옴).
 *   @param {String} savedData - 마지막으로 성공적으로 저장된 상태의 JSON 문자열 (비교용).
 *   @param {Object} fullData - 백엔드에서 가져온 원래의 전체 상태 (컨텍스트용).
 * - 반환값: {Array} API 요청 객체들의 목록.
 */
export function getSettingList(data, savedData, fullData) {
  const res = [];

  const tobeSaved = cloneObject(data);
  if (JSON.stringify(tobeSaved) === savedData) {
    return res;
  }
  const saved = JSON.parse(savedData);
  const original = cloneObject(fullData);

  if (isModbusGateway(original.dev_model)) {
    // 1. Process Modbus/HMI Events
    if (tobeSaved.ports) {
      tobeSaved.ports.forEach((slv) => {
        const urlNamespace = slv.query_type === 'MB' ? 'modbus-slaves' : 'hmis';
        const urlSuffix = `${urlNamespace}/${slv.port_id}`;

        const originalPort = original.ports.find(
          (p) => p.port_id === slv.port_id
        );
        if (originalPort) {
          _processEvents(
            slv.events,
            originalPort,
            urlSuffix,
            res,
            urlNamespace,
            true
          );
        }
      });
    }

    // 2. Process Modbus/HMI Slave configurations
    _processSlaves(tobeSaved.mb_ports, original.dev_id, original, res);
  } else {
    // 1. Process Standard Port Events
    if (tobeSaved.ports) {
      tobeSaved.ports.forEach((port) => {
        const originalPort = original.ports.find(
          (p) => p.port_id === port.port_id
        );
        if (originalPort) {
          _processEvents(
            port.events,
            originalPort,
            `ports/${port.port_type.toLowerCase()}/${port.port_id}`,
            res,
            port.port_type.toLowerCase(),
            false
          );
        }
      });
    }

    // 2. Process Standard Port Schedules
    if (tobeSaved.ports) {
      tobeSaved.ports.forEach((port) => {
        const originalPort = original.ports.find(
          (p) => p.port_id === port.port_id
        );
        if (originalPort) {
          _processSchedules(port.schedules, originalPort, res);
        }
      });
    }

    // 3. Process General Port Settings (Name, Enable, Widget, Color, etc.)
    if (tobeSaved.ports) {
      tobeSaved.ports.forEach((port) => {
        const originalPort = original.ports.find(
          (p) => p.port_id === port.port_id
        );
        if (!originalPort) return;

        const urlSuffix = `ports/${port.port_type.toLowerCase()}/${
          port.port_id
        }`;

        // We delete sub-resources before comparison to avoid duplicate updates
        delete port.events;
        delete port.schedules;
        const url = `${API_PREFIX}/users/me/${urlSuffix}`;
        delete port.port_type;
        delete port.port_id;

        for (const prop in port) {
          if (Object.prototype.hasOwnProperty.call(port, prop)) {
            if (
              JSON.stringify(port[prop]) !== JSON.stringify(originalPort[prop])
            ) {
              res.push({
                relative_url: url,
                method: 'PUT',
                body: port,
              });
              break;
            }
          }
        }
      });
    }
  }

  delete tobeSaved.ports;
  delete tobeSaved.mb_ports;
  delete tobeSaved.dev_client_id;
  for (const prop in tobeSaved) {
    if (Object.prototype.hasOwnProperty.call(tobeSaved, prop)) {
      if (JSON.stringify(tobeSaved[prop]) !== JSON.stringify(saved[prop])) {
        res.push({
          relative_url: `${API_PREFIX}/users/me/devices/${original.dev_model.toLowerCase()}/${
            original.dev_id
          }`,
          method: 'PUT',
          body: tobeSaved,
        });
        break;
      }
    }
  }
  return res;
}

/* --- INTERNAL BUFFER HELPERS --- */

/**
 * [DEV] Internal Helper: Builds the specific payload for a SIG Standard Port.
 * SIG 표준 포트에 대한 특정 페이로드를 생성하는 내부 헬퍼입니다.
 */
function _preparePortPayload(port, refPort) {
  const basePortPayload = {
    port_id: port.port_id,
    port_type: port.port_type,
    port_desc: port.port_desc,
    ui_display: port.ui_display,
    ui_widget: port.ui_widget,
    ui_color: port.ui_color,
  };

  switch (port.port_type) {
    case TEMPERATURE:
      return {
        ...basePortPayload,
        display_min: port.display_min,
        display_max: port.display_max,
        /* [DEV] Temperature payload extension point */
        events: getEventSetting(port.events, true, refPort.events),
      };
    case ANALOG_INPUT:
      return {
        ...basePortPayload,
        port_min: port.port_min,
        port_max: port.port_max,
        dec_place: port.dec_place,
        unit: port.unit,
        display_min: port.display_min,
        display_max: port.display_max,
        /* [DEV] Analog Input payload extension point */
        events: getEventSetting(port.events, true, refPort.events),
      };
    case DIGITAL_INPUT:
      return {
        ...basePortPayload,
        /* [DEV] Digital Input payload extension point */
        events: getEventSetting(port.events, false, refPort.events),
      };
    case DIGITAL_OUTPUT:
      return {
        ...basePortPayload,
        relay_mode: port.relay_mode,
        commands: port.commands,
        /* [DEV] Digital Output payload extension point */
        events: getEventSetting(port.events, false, refPort.events),
        schedules: getScheduleSetting(port.schedules, refPort.schedules),
      };
    default:
      return basePortPayload;
  }
}

/**
 * [DEV] Internal Helper: Builds the specific payload for a Modbus Gateway.
 * 모드버스 게이트웨이에 대한 특정 페이로드를 생성하는 내부 헬퍼입니다.
 */
function _prepareModbusPayload(portData, reference) {
  return {
    CLOUD: {
      dev_desc: portData.dev_desc,
      modbus_mode: portData.modbus_mode,
      modbus_timeout: portData.modbus_timeout,
      modbus_interframe_gap: portData.modbus_interframe_gap,
      serial_baudrate: portData.serial_baudrate,
      serial_parity: portData.serial_parity,
      serial_data_bit: portData.serial_data_bit,
      serial_stop_bit: portData.serial_stop_bit,
      mb_ports: getSlaveSetting(portData.ports, reference.ports),
      ports: portData.ports
        .filter((port) => port.action !== STS.ADD && port.action !== STS.DELETE)
        .map((port) => {
          const refPort = reference.ports.find(
            (p) => p.port_id === port.port_id
          );
          return {
            port_id: port.port_id,
            query_type: port.query_type,
            events: getEventSetting(
              port.events,
              true,
              (refPort && refPort.events) || []
            ),
          };
        }),
    },
  };
}

/**
 * [DEV] API Payload Preparation (getModifiedSetting)
 *
 * [English]
 * - Purpose: Extracts the raw state of the UI Settings (handled by Vuex/components)
 *            and formats it into the exact JSON structure required by the Sollae Cloud Backend (CLOUD)
 *            and Device Hardware (DEVICE).
 * - Design Pattern: If a UI field has a 'ref' property in the template (e.g. Min scale refs Max scale),
 *                   they are treated as a combined UI unit, but both codes should still be present
 *                   in the `portData` object for extraction here.
 *
 * [한국어]
 * - 설명: API 페이로드 준비 (getModifiedSetting):
 *         이 함수는 UI 설정(Vuex/컴포넌트에서 관리)의 원시 상태를 추출하여
 *         Sollae Cloud 백엔드(CLOUD) 및 장치 하드웨어(DEVICE)에서 요구하는
 *         정확한 JSON 구조로 형식을 지정합니다.
 * - 디자인 패턴: 템플릿에 'ref' 속성이 있는 경우 UI 필드는 결합된 단위로 처리되지만,
 *                추출을 위해 portData 객체에는 여전히 두 코드가 모두 존재해야 합니다.
 */
export function getModifiedSetting(pData, reference) {
  const portData = Object.freeze(cloneObject(pData));
  const deviceSetting = {};

  // Extract specific hardware settings for DI ports (DEVICE config)
  portData.ports
    .filter((el) => el.port_type === DIGITAL_INPUT)
    .forEach((port) => {
      deviceSetting[(port.port_name + '').toUpperCase()] = {
        count_def: port.count_def,
        count_dir: port.count_dir,
        count_type: port.count_type,
        valid_time: port.valid_time,
      };
    });

  // Handle Modbus Gateway specifics
  if (isModbusGateway(portData.dev_model)) {
    return _prepareModbusPayload(portData, reference);
  }

  // Handle Standard Devices (SIG)
  return {
    // Passed for cloud side configuration (settings will be stored in cloud database)
    CLOUD: {
      dev_client_id: portData.dev_client_id,
      dev_desc: portData.dev_desc, // General Device Name
      ports: portData.ports.map((port) => {
        const refPort = reference.ports.find((p) => p.port_id === port.port_id);
        return _preparePortPayload(port, refPort);
      }),
    },
    DEVICE: deviceSetting, // Passed for device side configuration (settings will be stored in device memory)
  };
}

/**
 * [DEV] Critical Entry Point: createDeviceSettingData
 * 장치 모델을 기반으로 초기 설정 템플릿을 생성하는 핵심 진입점 함수입니다.
 *
 * [English]
 * - Purpose: This is one of the first functions called when the Device Settings menu opens.
 * - Logic: Matches the device model to its specific UI template factory (e.g., SIG-5431, SMG-5511).
 * - Usage: Used by `DeviceSettingMenu.vue` to initialize the `configData` state.
 *
 * [한국어]
 * - 설명: 장치 설정 메뉴가 열릴 때 가장 먼저 호출되는 핵심 함수 중 하나입니다.
 * - 로직: 장치 모델을 특정 UI 템플릿 팩토리(예: SIG-5431, SMG-5511)와 매핑합니다.
 * - 용도: `DeviceSettingMenu.vue`에서 `configData` 상태를 초기화하는 데 사용됩니다.
 */
export function createDeviceSettingData(model) {
  switch (model) {
    case SOLLAE_MODEL.SIG_5431:
      return createSIG5431Template();
    case SOLLAE_MODEL.SIG_5441:
      return createSIG5441Template();
    case SOLLAE_MODEL.SIG_5451:
      return createSIG5451Template();
    case SOLLAE_MODEL.SIG_5601:
      return createSIG5601Template();
    case SOLLAE_MODEL.SIG_5561:
      return createSIGThermoTemplate();
    case SOLLAE_MODEL.SMG_5511:
    case SOLLAE_MODEL.SMG_5521:
      return createSMGTemplate();
  }
}
